// Compresses a receipt/screenshot image for the parse-receipt Edge Function:
// downscale to a long edge of 1568px (the resolution vision models are
// optimized for) and re-encode as JPEG. Returns bare base64 without the
// data-URI prefix, which is what the function expects.
const MAX_LONG_EDGE = 1568;
const JPEG_QUALITY = 0.72;
// Reject absurd originals before decode; the function itself enforces the
// post-compression ceiling.
const MAX_SOURCE_BYTES = 15 * 1024 * 1024;

export async function compressReceiptImage(
  file: File
): Promise<{ base64: string; mediaType: 'image/jpeg' } | { error: 'image_too_large' | 'unsupported_image' }> {
  if (file.size > MAX_SOURCE_BYTES) return { error: 'image_too_large' };
  if (!file.type.startsWith('image/')) return { error: 'unsupported_image' };

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_LONG_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { error: 'unsupported_image' };
    // White backdrop so transparent PNG screenshots don't turn black in JPEG.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    );
    if (!blob) return { error: 'unsupported_image' };

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });

    return { base64: dataUrl.slice(dataUrl.indexOf(',') + 1), mediaType: 'image/jpeg' };
  } catch (err) {
    console.error('compressReceiptImage failed:', err);
    return { error: 'unsupported_image' };
  }
}
